/**
 * Is it safe to replace the running app right now?
 *
 * WHY THIS EXISTS
 * ---------------
 * sw-register reloads the page to move a tab off a stale build. Its first
 * version reloaded as soon as the tab went HIDDEN, on the assumption that hidden
 * means the student has finished. It does not. On mobile, hidden also fires when
 * they switch apps or the screen locks, which is something a student does
 * constantly during a two-hour practice test. A cross-vendor review called that
 * a ship blocker and it was right: the sequence "answer question 27 -> open the
 * calculator app -> come back to a reloaded page" is entirely ordinary.
 *
 * So "the tab is hidden" is not the safety property. The property is:
 *
 *   nothing is in progress that a reload would damage, AND
 *   nothing is still being written to disk.
 *
 * TWO SIGNALS
 * -----------
 * 1. Route. Derived from the URL rather than from components calling begin/end,
 *    because a component that forgets to call `end` on an error path leaves the
 *    app permanently un-updatable, and a component that forgets to call `begin`
 *    silently reopens the bug. The URL cannot drift out of sync with itself.
 *
 * 2. In-flight writes. An IndexedDB transaction is only durable once it
 *    COMMITS, and a request reporting success is not a commit. Reloading between
 *    the two loses the write. The offline queue counts its own transactions here.
 */

/** Routes where a reload would interrupt work or lose progress. */
const CRITICAL_PREFIXES = [
  "/app/drill/",
  "/app/practice-test/run/",
  "/app/act-test",
  "/app/diagnostic",
  "/app/hidden-test",
  "/app/calc-rules",
  "/app/calc-bc-rules",
  "/app/csa-trace",
  "/app/inference-calculations",
  "/app/mistakes/",
  // Timed trainers: short, but a reload mid-attempt loses the run.
  "/app/pace",
  "/app/mental-math",
  "/app/formula",
  "/app/graph-gym",
];

export function isCriticalRoute(pathname: string): boolean {
  return CRITICAL_PREFIXES.some((p) => pathname.startsWith(p));
}

let inFlightWrites = 0;

/**
 * Wrap a durable write. Resolves/rejects exactly as the input does; the count is
 * decremented in both cases so a failed write cannot wedge updates forever.
 */
export function trackWrite<T>(p: Promise<T>): Promise<T> {
  inFlightWrites += 1;
  return p.finally(() => {
    inFlightWrites = Math.max(0, inFlightWrites - 1);
  });
}

export function pendingWrites(): number {
  return inFlightWrites;
}

/**
 * The gate sw-register asks before reloading.
 *
 * Deliberately conservative: when in doubt it returns false and the update waits
 * for the next opportunity. A missed update costs a student nothing, because the
 * next cold launch picks it up anyway. A wrong reload costs them an answer.
 */
export function isSafeToReplaceApp(pathname?: string): boolean {
  if (inFlightWrites > 0) return false;
  const path = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  return !isCriticalRoute(path);
}
